 import React, { useState } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useQuery, useMutation } from '@apollo/client';
import {
  ArrowLeft,
  Building2,
  ArrowRight,
  AlertCircle,
  Loader2,
  Save,
  Edit,
  Target,
  DollarSign,
  Calendar,
  X,
  Globe,
  Phone,
} from 'lucide-react';
import { cn, formatDate } from '@/lib/utils';
import { useAuth } from '@/contexts/AuthContext';
import { RichTextEditor } from '@/components/ui/RichTextEditor';
import { ActivitiesTab } from '@/pages/clients/components/ActivitiesTab';
import { ContactsTab } from '@/pages/clients/components/ContactsTab';
import { FilesTab } from '@/pages/clients/components/FilesTab';
import {
  GET_PROSPECT,
  UPDATE_PROSPECT,
  DELETE_PROSPECT,
  type ProspectQueryResult,
  type UpdateProspectResult,
} from '@/graphql/prospects';
import {
  CREATE_OPPORTUNITY,
  type CreateOpportunityResult,
} from '@/graphql/opportunities';
import { type Contact } from '@/graphql/organizations';

type TabId = 'overview' | 'contacts' | 'activities' | 'files';

interface Tab {
  id: TabId;
  label: string;
}

const TABS: Tab[] = [
  { id: 'overview', label: 'Overview' },
  { id: 'contacts', label: 'Contacts' },
  { id: 'activities', label: 'Activities' },
  { id: 'files', label: 'Files' },
];

const OPPORTUNITY_STAGES = [
  { value: 'discovery', label: 'Discovery' },
  { value: 'proposal', label: 'Proposal' },
  { value: 'negotiation', label: 'Negotiation' },
];

export function ProspectDetail() {
  const { prospectId } = useParams();
  const navigate = useNavigate();
  const { canWrite } = useAuth();
  const [activeTab, setActiveTab] = useState<TabId>('overview');
  
  // Modal states
  const [showEditModal, setShowEditModal] = useState(false);
  const [showCreateOpportunityModal, setShowCreateOpportunityModal] = useState(false);
  
  // Description editing state
  const [description, setDescription] = useState<string>('');
  const [isDescriptionDirty, setIsDescriptionDirty] = useState(false);
  const [savingDescription, setSavingDescription] = useState(false);

  // Edit form state
  const [editName, setEditName] = useState('');
  const [editType, setEditType] = useState<'CFI' | 'PSP'>('CFI');
  const [editWebsite, setEditWebsite] = useState('');
  const [editPhone, setEditPhone] = useState('');

  // Create opportunity form state
  const [oppValue, setOppValue] = useState('');
  const [oppStage, setOppStage] = useState('discovery');
  const [oppProbability, setOppProbability] = useState('25');
  const [oppExpectedClose, setOppExpectedClose] = useState('');
  const [oppNotes, setOppNotes] = useState('');
  const [oppIsRecurring, setOppIsRecurring] = useState(false);

  // GraphQL query
  const { data, loading, error, refetch } = useQuery<ProspectQueryResult>(GET_PROSPECT, {
    variables: { id: prospectId },
    skip: !prospectId,
    fetchPolicy: 'cache-and-network',
    onCompleted: (data) => {
      // Initialize description when data loads
      if (data?.prospect?.description) {
        setDescription(data.prospect.description);
      }
    },
  });

  // GraphQL mutations
  const [updateProspect, { loading: updating }] = useMutation<UpdateProspectResult>(UPDATE_PROSPECT, {
    onCompleted: () => {
      setIsDescriptionDirty(false);
      setSavingDescription(false);
      setShowEditModal(false);
      refetch();
    },
    onError: () => {
      setSavingDescription(false);
    },
  });

  const [deleteProspect, { loading: deleting }] = useMutation(DELETE_PROSPECT, {
    onCompleted: () => {
      navigate('/sales/prospects');
    },
  });

  const [createOpportunity, { loading: creatingOpportunity }] = useMutation<CreateOpportunityResult>(CREATE_OPPORTUNITY, {
    onCompleted: () => {
      setShowCreateOpportunityModal(false);
      navigate('/sales/opportunities');
    },
    onError: (error) => {
      alert(`Error creating opportunity: ${error.message}`);
    },
  });

  const prospect = data?.prospect;
  const primaryContact = prospect?.primaryContact || prospect?.contacts?.[0];

  const handleDescriptionChange = (html: string) => {
    setDescription(html);
    setIsDescriptionDirty(true);
  };

  const handleSaveDescription = () => {
    if (!prospectId) return;
    setSavingDescription(true);
    updateProspect({
      variables: {
        id: prospectId,
        input: {
          description,
        },
      },
    });
  };

  const handleOpenEditModal = () => {
    if (prospect) {
      setEditName(prospect.name);
      setEditType(prospect.type);
      setEditWebsite(prospect.website || '');
      setEditPhone(prospect.phone || '');
    }
    setShowEditModal(true);
  };

  const handleSaveEdit = () => {
    if (!prospectId || !editName.trim()) return;
    updateProspect({
      variables: {
        id: prospectId,
        input: {
          name: editName,
          type: editType,
          website: editWebsite || undefined,
          phone: editPhone || undefined,
        },
      },
    });
  };

  // Format phone number as (XXX) XXX-XXXX
  const formatPhoneNumber = (value: string): string => {
    // Remove all non-digits
    const digits = value.replace(/\D/g, '');
    
    // Limit to 10 digits
    const limited = digits.slice(0, 10);
    
    // Format based on length
    if (limited.length === 0) return '';
    if (limited.length <= 3) return `(${limited}`;
    if (limited.length <= 6) return `(${limited.slice(0, 3)}) ${limited.slice(3)}`;
    return `(${limited.slice(0, 3)}) ${limited.slice(3, 6)}-${limited.slice(6)}`;
  };

  const handlePhoneChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const formatted = formatPhoneNumber(e.target.value);
    setEditPhone(formatted);
  };

  // Format currency with commas (e.g., 1000000 → 1,000,000)
  const formatCurrencyInput = (value: string): string => {
    // Remove all non-digits
    const digits = value.replace(/\D/g, '');
    // Add commas for thousands
    return digits.replace(/\B(?=(\d{3})+(?!\d))/g, ',');
  };

  // Parse formatted currency back to number
  const parseCurrencyInput = (value: string): number => {
    return parseFloat(value.replace(/,/g, '')) || 0;
  };

  const handleDealValueChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const formatted = formatCurrencyInput(e.target.value);
    setOppValue(formatted);
  };

  const handleOpenCreateOpportunity = () => {
    // Set default expected close date to 30 days from now
    const defaultClose = new Date();
    defaultClose.setDate(defaultClose.getDate() + 30);
    setOppExpectedClose(defaultClose.toISOString().split('T')[0]);
    setOppValue('');
    setOppStage('discovery');
    setOppProbability('25');
    setOppNotes('');
    setOppIsRecurring(false);
    setShowCreateOpportunityModal(true);
  };

  const handleCreateOpportunity = () => {
    if (!prospectId || !prospect) return;
    
    createOpportunity({
      variables: {
        input: {
          organizationId: prospectId,
          name: `${prospect.name} - Opportunity`,
          value: parseCurrencyInput(oppValue),
          stage: oppStage.toUpperCase(),
          probability: parseInt(oppProbability) || 25,
          isRecurring: oppIsRecurring,
          expectedCloseDate: oppExpectedClose,
          notes: oppNotes || undefined,
        },
      },
    });
  };

  const handleDelete = () => {
    if (!prospectId) return;
    if (confirm('Are you sure you want to delete this prospect?')) {
      deleteProspect({
        variables: { id: prospectId },
      });
    }
  };

  // Loading state
  if (loading && !prospect) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-pond" />
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div className="card p-8 text-center">
        <AlertCircle className="w-8 h-8 text-red-500 mx-auto mb-4" />
        <p className="text-red-600 dark:text-red-400 mb-4">
          Error loading prospect: {error.message}
        </p>
        <div className="flex justify-center gap-3">
          <Link to="/sales/prospects" className="btn-secondary">
            <ArrowLeft className="w-4 h-4" />
            Back to Prospects
          </Link>
          <button onClick={() => refetch()} className="btn-primary">
            Retry
          </button>
        </div>
      </div>
    );
  }

  // Not found state
  if (!prospect) {
    return (
      <div className="card p-8 text-center">
        <p className="text-slate dark:text-mist">Prospect not found</p>
        <Link to="/sales/prospects" className="btn-secondary mt-4">
          <ArrowLeft className="w-4 h-4" />
          Back to Prospects
        </Link>
      </div>
    );
  }

  const renderTabContent = () => {
    switch (activeTab) {
      case 'overview':
        return (
          <OverviewTab
            prospect={prospect}
            description={description}
            onDescriptionChange={handleDescriptionChange}
            isDescriptionDirty={isDescriptionDirty}
            onSaveDescription={handleSaveDescription}
            savingDescription={savingDescription}
            canWrite={canWrite('sales')}
          />
        );
      case 'contacts':
        return (
          <ContactsTab
            clientId={prospect.id}
            contacts={prospect.contacts as unknown as Contact[]}
            onRefresh={() => refetch()}
          />
        );
      case 'activities':
        return (
          <ActivitiesTab
            clientId={prospect.id}
            clientName={prospect.name}
            contactEmail={primaryContact?.email || ''}
          />
        );
      case 'files':
        return <FilesTab clientId={prospect.id} />;
      default:
        return null;
    }
  };

  return (
    <div className="space-y-6">
      {/* Breadcrumb & Header */}
      <div className="flex items-center gap-4">
        <Link to="/sales/prospects" className="btn-ghost btn-icon">
          <ArrowLeft className="w-5 h-5" />
        </Link>
        <div className="flex-1">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 rounded-xl bg-pond/10 dark:bg-pond-light/20 flex items-center justify-center">
              <Building2 className="w-6 h-6 text-pond dark:text-pond-light" />
            </div>
            <div>
              <h1 className="text-2xl font-semibold text-slate dark:text-mist">
                {prospect.name}
              </h1>
              <div className="flex items-center gap-2 mt-1">
                <span className="badge-primary">{prospect.type}</span>
                <span className="badge badge-warning">Prospect</span>
                <span className="text-sm text-chalkboard dark:text-rain-cloud">
                  Added {formatDate(new Date(prospect.createdAt))}
                </span>
              </div>
            </div>
          </div>
        </div>
        
        {/* Action Buttons */}
        <div className="flex items-center gap-2">
          <button
            onClick={handleDelete}
            className="btn-secondary text-red-600 hover:text-red-700 dark:text-red-400"
            disabled={deleting}
          >
            {deleting ? 'Deleting...' : 'Delete'}
          </button>
          {canWrite('sales') && (
            <>
              <button onClick={handleOpenEditModal} className="btn-secondary">
                <Edit className="w-4 h-4" />
                Edit
              </button>
              <button
                onClick={handleOpenCreateOpportunity}
                className="btn-primary"
              >
                <Target className="w-4 h-4" />
                Create Opportunity
              </button>
            </>
          )}
        </div>
      </div>

      {/* Tabs Navigation */}
      <div className="tabs">
        {TABS.map((tab) => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id)}
            className={cn('tab', activeTab === tab.id && 'active')}
          >
            {tab.label}
          </button>
        ))}
      </div>

      {/* Tab Content */}
      <div className="animate-fade-in">
        {renderTabContent()}
      </div>

      {/* Edit Prospect Modal */}
      {showEditModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
          <div
            className="absolute inset-0 bg-black/50"
            onClick={() => setShowEditModal(false)}
          />
          <div className="relative bg-white dark:bg-navy rounded-lg shadow-xl max-w-md w-full mx-4 animate-slide-in">
            <div className="p-6">
              <div className="flex items-center justify-between mb-6">
                <div className="flex items-center gap-2">
                  <Edit className="w-5 h-5 text-pond dark:text-pond-light" />
                  <h2 className="text-xl font-semibold text-slate dark:text-mist">
                    Edit Prospect
                  </h2>
                </div>
                <button
                  onClick={() => setShowEditModal(false)}
                  className="btn-ghost btn-icon btn-sm"
                >
                  <X className="w-4 h-4" />
                </button>
              </div>

              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-slate dark:text-mist mb-1">
                    Company Name <span className="text-sunset">*</span>
                  </label>
                  <input
                    type="text"
                    value={editName}
                    onChange={(e) => setEditName(e.target.value)}
                    className="input"
                    placeholder="Company name"
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-slate dark:text-mist mb-1">
                    Type
                  </label>
                  <select
                    value={editType}
                    onChange={(e) => setEditType(e.target.value as 'CFI' | 'PSP')}
                    className="select"
                  >
                    <option value="CFI">CFI - Bank/Credit Union</option>
                    <option value="PSP">PSP - Service Provider</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-slate dark:text-mist mb-1">
                    Website
                  </label>
                  <div className="flex items-center gap-2">
                    <Globe className="w-4 h-4 text-french-gray flex-shrink-0" />
                    <input
                      type="text"
                      value={editWebsite}
                      onChange={(e) => setEditWebsite(e.target.value)}
                      placeholder="www.example.com"
                      className="input flex-1"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-slate dark:text-mist mb-1">
                    Phone
                  </label>
                  <div className="flex items-center gap-2">
                    <Phone className="w-4 h-4 text-french-gray flex-shrink-0" />
                    <input
                      type="tel"
                      value={editPhone}
                      onChange={handlePhoneChange}
                      placeholder="(555) 555-5555"
                      className="input flex-1"
                    />
                  </div>
                </div>
              </div>

              <div className="flex justify-end gap-3 mt-6 pt-4 border-t border-gray dark:border-night-pond">
                <button
                  onClick={() => setShowEditModal(false)}
                  className="btn-secondary"
                >
                  Cancel
                </button>
                <button
                  onClick={handleSaveEdit}
                  disabled={!editName.trim() || updating}
                  className="btn-primary"
                >
                  {updating ? (
                    <>
                      <Loader2 className="w-4 h-4 animate-spin" />
                      Saving...
                    </>
                  ) : (
                    <>
                      <Save className="w-4 h-4" />
                      Save Changes
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Create Opportunity Modal */}
      {showCreateOpportunityModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
          <div
            className="absolute inset-0 bg-black/50"
            onClick={() => setShowCreateOpportunityModal(false)}
          />
          <div className="relative bg-white dark:bg-navy rounded-lg shadow-xl max-w-lg w-full mx-4 animate-slide-in">
            <div className="p-6">
              <div className="flex items-center justify-between mb-6">
                <div className="flex items-center gap-2">
                  <Target className="w-5 h-5 text-violet dark:text-violet-light" />
                  <h2 className="text-xl font-semibold text-slate dark:text-mist">
                    Create Opportunity
                  </h2>
                </div>
                <button
                  onClick={() => setShowCreateOpportunityModal(false)}
                  className="btn-ghost btn-icon btn-sm"
                >
                  <X className="w-4 h-4" />
                </button>
              </div>

              {/* Prospect Info Banner */}
              <div className="bg-pale-gray-1 dark:bg-night-pond rounded-lg p-4 mb-6">
                <p className="text-sm text-chalkboard dark:text-rain-cloud">Creating opportunity for:</p>
                <p className="font-semibold text-slate dark:text-mist">{prospect.name}</p>
              </div>

              <div className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-slate dark:text-mist mb-1">
                      Deal Value <span className="text-sunset">*</span>
                    </label>
                    <div className="relative">
                      <DollarSign className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-french-gray pointer-events-none z-10" />
                      <input
                        type="text"
                        value={oppValue}
                        onChange={handleDealValueChange}
                        placeholder="0"
                        className="input"
                        style={{ paddingLeft: '2.5rem' }}
                      />
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-slate dark:text-mist mb-1">
                      Stage
                    </label>
                    <select
                      value={oppStage}
                      onChange={(e) => setOppStage(e.target.value)}
                      className="select"
                    >
                      {OPPORTUNITY_STAGES.map((stage) => (
                        <option key={stage.value} value={stage.value}>
                          {stage.label}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-slate dark:text-mist mb-1">
                      Probability (%)
                    </label>
                    <input
                      type="number"
                      value={oppProbability}
                      onChange={(e) => setOppProbability(e.target.value)}
                      placeholder="25"
                      className="input"
                      min="0"
                      max="100"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-slate dark:text-mist mb-1">
                      Expected Close Date
                    </label>
                    <div className="relative">
                      <Calendar className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-french-gray pointer-events-none z-10" />
                      <input
                        type="date"
                        value={oppExpectedClose}
                        onChange={(e) => setOppExpectedClose(e.target.value)}
                        className="input"
                        style={{ paddingLeft: '2.5rem' }}
                      />
                    </div>
                  </div>
                </div>

                {/* Annual Recurring Toggle */}
                <label className="flex items-center gap-3 p-3 rounded-lg border border-gray dark:border-night-pond cursor-pointer hover:bg-pale-gray-1 dark:hover:bg-night-pond/50 transition-colors">
                  <input
                    type="checkbox"
                    checked={oppIsRecurring}
                    onChange={(e) => setOppIsRecurring(e.target.checked)}
                    className="w-4 h-4 rounded border-gray dark:border-night-pond text-pond dark:text-pond-light focus:ring-pond"
                  />
                  <div>
                    <span className="text-sm font-medium text-slate dark:text-mist">
                      Annual Recurring Relationship
                    </span>
                    <p className="text-xs text-chalkboard dark:text-rain-cloud">
                      Check this if the deal value recurs annually
                    </p>
                  </div>
                </label>

                <div>
                  <label className="block text-sm font-medium text-slate dark:text-mist mb-1">
                    Notes
                  </label>
                  <textarea
                    value={oppNotes}
                    onChange={(e) => setOppNotes(e.target.value)}
                    placeholder="Additional notes about this opportunity..."
                    rows={3}
                    className="input resize-none"
                  />
                </div>
              </div>

              <div className="flex justify-end gap-3 mt-6 pt-4 border-t border-gray dark:border-night-pond">
                <button
                  onClick={() => setShowCreateOpportunityModal(false)}
                  className="btn-secondary"
                >
                  Cancel
                </button>
                <button
                  onClick={handleCreateOpportunity}
                  disabled={!oppValue || parseCurrencyInput(oppValue) <= 0 || creatingOpportunity}
                  className="btn-primary"
                >
                  {creatingOpportunity ? (
                    <>
                      <Loader2 className="w-4 h-4 animate-spin" />
                      Creating...
                    </>
                  ) : (
                    <>
                      <ArrowRight className="w-4 h-4" />
                      Create Opportunity
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ==================== Overview Tab ====================

interface OverviewTabProps {
  prospect: NonNullable<ProspectQueryResult['prospect']>;
  description: string;
  onDescriptionChange: (html: string) => void;
  isDescriptionDirty: boolean;
  onSaveDescription: () => void;
  savingDescription: boolean;
  canWrite: boolean;
}

function OverviewTab({
  prospect,
  description,
  onDescriptionChange,
  isDescriptionDirty,
  onSaveDescription,
  savingDescription,
  canWrite,
}: OverviewTabProps) {
  const primaryContact = prospect.primaryContact || prospect.contacts?.[0];

  return (
    <div className="space-y-6">
      {/* Quick Info Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className="card">
          <div className="card-body">
            <p className="text-sm text-chalkboard dark:text-rain-cloud">Type</p>
            <p className="text-lg font-medium text-slate dark:text-mist">
              {prospect.type === 'CFI' ? 'Bank/Credit Union' : 'Service Provider'}
            </p>
          </div>
        </div>
        <div className="card">
          <div className="card-body">
            <p className="text-sm text-chalkboard dark:text-rain-cloud">Primary Contact</p>
            <p className="text-lg font-medium text-slate dark:text-mist">
              {primaryContact
                ? `${primaryContact.firstName} ${primaryContact.lastName}`
                : 'Not set'}
            </p>
            {primaryContact?.email && (
              <a
                href={`mailto:${primaryContact.email}`}
                className="text-sm text-pond dark:text-pond-light hover:underline block"
              >
                {primaryContact.email}
              </a>
            )}
            {primaryContact?.phone && (
              <a
                href={`tel:${primaryContact.phone}`}
                className="text-sm text-pond dark:text-pond-light hover:underline block"
              >
                {primaryContact.phone}
              </a>
            )}
          </div>
        </div>
        <div className="card">
          <div className="card-body">
            <p className="text-sm text-chalkboard dark:text-rain-cloud">Website & Phone</p>
            {prospect.website ? (
              <a
                href={prospect.website.startsWith('http') ? prospect.website : `https://${prospect.website}`}
                target="_blank"
                rel="noopener noreferrer"
                className="text-lg font-medium text-pond dark:text-pond-light hover:underline block"
              >
                {prospect.website}
              </a>
            ) : (
              <p className="text-lg font-medium text-chalkboard dark:text-rain-cloud">No website</p>
            )}
            {prospect.phone ? (
              <a
                href={`tel:${prospect.phone}`}
                className="text-sm text-pond dark:text-pond-light hover:underline block mt-1"
              >
                {prospect.phone}
              </a>
            ) : (
              <p className="text-sm text-chalkboard dark:text-rain-cloud mt-1">No phone</p>
            )}
          </div>
        </div>
      </div>

      {/* Narrative / Description */}
      <div className="card">
        <div className="card-header flex items-center justify-between">
          <div>
            <h3 className="font-semibold text-slate dark:text-mist">Prospect Narrative</h3>
            <p className="text-sm text-chalkboard dark:text-rain-cloud">
              Notes, background information, and context about this prospect
            </p>
          </div>
          {canWrite && isDescriptionDirty && (
            <button
              onClick={onSaveDescription}
              disabled={savingDescription}
              className="btn-primary btn-sm"
            >
              {savingDescription ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin" />
                  Saving...
                </>
              ) : (
                <>
                  <Save className="w-4 h-4" />
                  Save
                </>
              )}
            </button>
          )}
        </div>
        <div className="card-body p-0">
          {canWrite ? (
            <RichTextEditor
              content={description}
              onChange={onDescriptionChange}
              placeholder="Add notes about this prospect..."
              className="border-0 shadow-none"
            />
          ) : description ? (
            <div
              className="prose prose-sm dark:prose-invert max-w-none p-4"
              dangerouslySetInnerHTML={{ __html: description }}
            />
          ) : (
            <p className="text-chalkboard dark:text-rain-cloud italic p-4">
              No description available
            </p>
          )}
        </div>
      </div>
    </div>
  );
}
